/**
 * SEO-хелперы: канонические URL, JSON-LD, ссылки на магазины.
 *
 * Импортируется и на сервере, и в роутах. Никаких зависимостей от config.js
 * (он запрещён на клиенте), поэтому базовый домен задан константой.
 *
 * @see docs/seo/SEO-STRATEGY.md
 */

import { hasReviews } from '../data/reviews';

/** Канонический хост. Только https и без www. */
export const SITE_URL = 'https://new-tab.studio';

export const SITE_NAME = 'New Tab Studio';

/**
 * Картинка для соцсетей по умолчанию. Логотип в этой роли показывает
 * человеку белый квадрат с надписью; скриншот показывает продукт.
 *
 * Путь /img, а не /screenshots: последний перехватывает nginx и отдаёт
 * из тома с превью тем, то есть файлы из образа туда не попадают.
 * @see DEPLOY.md
 */
export const DEFAULT_OG_IMAGE = `${'https://new-tab.studio'}/img/widgets/og-default.jpg`;

export const TITLE_SUFFIX = ' | New Tab Studio';

export const CHROME_STORE_URL =
	'https://chrome.google.com/webstore/detail/new-tab-studio-countdown/epngggilgnflanfabeldfpbgponcgpgl';

export const FIREFOX_ADDON_URL =
	'https://addons.mozilla.org/ru/firefox/addon/new-tab-studio-countdown/?src=site';

/**
 * Публичные цифры из Chrome Web Store. Держать в актуальном состоянии:
 * они уходят в JSON-LD (aggregateRating), а врать в разметке нельзя —
 * это прямой повод для ручных санкций.
 *
 * Проверено: 11.09.2026 — 40 000 пользователей, 4.9 из 5.
 *
 * ratingCount: карточка показывает «1.4K», точного числа она не даёт, так что
 * 1400 — это её округление, а не подсчёт. Для разметки допустимо, но при
 * сверке лучше брать цифру из Chrome Web Store Developer Dashboard.
 */
export const STORE_STATS = {
	users: 40000,
	ratingValue: 4.9,
	ratingCount: 1400,
	checkedAt: '2026-09-11',
};

/**
 * Приводит путь к каноническому виду: без query, без завершающего слэша,
 * в нижнем регистре хоста.
 *
 * @param {string} pathname
 * @returns {string} абсолютный URL
 */
export function canonicalUrl(pathname) {
	let path = (pathname || '/').split('?')[0].split('#')[0];

	if (path.length > 1 && path.endsWith('/')) {
		path = path.replace(/\/+$/, '');
	}

	if (!path.startsWith('/')) {
		path = `/${path}`;
	}

	// Корень отдаём со слэшем, чтобы canonical совпадал с тем, что в sitemap.xml
	return path === '/' ? `${SITE_URL}/` : `${SITE_URL}${path}`;
}

/**
 * Организация. Указывается один раз на главной, остальные страницы
 * ссылаются на неё через @id.
 */
export function organizationSchema() {
	return {
		'@context': 'https://schema.org',
		'@type': 'Organization',
		'@id': `${SITE_URL}/#organization`,
		name: SITE_NAME,
		url: SITE_URL,
		logo: `${SITE_URL}/logo.png`,
		sameAs: [CHROME_STORE_URL, FIREFOX_ADDON_URL],
	};
}

/**
 * Само расширение. Основной тип для этого сайта — это браузерное приложение,
 * а не товар, поэтому SoftwareApplication, а не Product.
 *
 * aggregateRating отдаётся, только если на странице реально показаны отзывы
 * (src/data/reviews.js). Разметка, расходящаяся с содержимым страницы, —
 * повод для ручных санкций, поэтому решение принимается автоматически,
 * а не флагом в роуте.
 *
 * @param {{ withRating?: boolean }} options withRating — намерение роута;
 *        фактический вывод дополнительно требует наличия отзывов.
 */
export function softwareApplicationSchema({ withRating = false } = {}) {
	const schema = {
		'@context': 'https://schema.org',
		'@type': 'SoftwareApplication',
		'@id': `${SITE_URL}/#app`,
		name: 'New Tab Studio: Countdown & widgets',
		url: SITE_URL,
		applicationCategory: 'BrowserApplication',
		operatingSystem: 'Chrome, Edge, Opera, Brave, Vivaldi',
		installUrl: CHROME_STORE_URL,
		description:
			'Replaces the browser new tab page with a customisable dashboard of draggable widgets: countdowns, clocks, to-do lists, habit trackers, notes, weather, photos and GIFs.',
		image: `${SITE_URL}/logo.png`,
		publisher: { '@id': `${SITE_URL}/#organization` },
		// Цены в разметку сознательно не кладём: они меняются, к ним применяются
		// купоны, и расхождение разметки с реальным чеком — повод для санкций.
		// Указываем только факт наличия бесплатной версии.
		offers: {
			'@type': 'Offer',
			price: '0',
			priceCurrency: 'USD',
			availability: 'https://schema.org/InStock',
		},
	};

	if (withRating && hasReviews()) {
		schema.aggregateRating = {
			'@type': 'AggregateRating',
			ratingValue: STORE_STATS.ratingValue,
			ratingCount: STORE_STATS.ratingCount,
			bestRating: 5,
			worstRating: 1,
		};
	}

	return schema;
}

/**
 * Хлебные крошки. items: [{ name, url }] — url относительный.
 *
 * @param {Array<{name: string, url: string}>} items
 */
export function breadcrumbSchema(items) {
	return {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: items.map((item, i) => ({
			'@type': 'ListItem',
			position: i + 1,
			name: item.name,
			item: canonicalUrl(item.url),
		})),
	};
}

/**
 * WebSite со строкой поиска по темам.
 */
export function webSiteSchema() {
	return {
		'@context': 'https://schema.org',
		'@type': 'WebSite',
		'@id': `${SITE_URL}/#website`,
		name: SITE_NAME,
		url: SITE_URL,
		publisher: { '@id': `${SITE_URL}/#organization` },
	};
}
